import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ActivityService, Activity } from '../activities/activity.service';
import { DeliveryService } from '../deliveries/delivery.service';
import { MaintenanceService, MaintenanceRecordDto, MaintenanceStatusDto } from '../maintenance/maintenance.service';
import { RiderService } from '../riders/rider.service';
import { VehicleService } from '../vehicles/vehicle.service';

@Component({
  selector: 'app-logistics-control-tower',
  templateUrl: './logistics-control-tower.component.html',
  styleUrls: ['./logistics-control-tower.component.scss']
})
export class LogisticsControlTowerComponent implements OnInit {
  loading = false;
  activities: Activity[] = [];
  deliveries: any[] = [];
  riders: any[] = [];
  vehicles: any[] = [];
  maintenance: MaintenanceStatusDto[] = [];
  records: MaintenanceRecordDto[] = [];

  constructor(
    private activityService: ActivityService,
    private deliveryService: DeliveryService,
    private riderService: RiderService,
    private vehicleService: VehicleService,
    private maintenanceService: MaintenanceService
  ) {}

  ngOnInit(): void {
    this.loadBoard();
  }

  loadBoard(): void {
    this.loading = true;
    forkJoin({
      activities: this.activityService.getAll(),
      deliveries: this.deliveryService.getToday(),
      riders: this.riderService.getRiders(),
      vehicles: this.vehicleService.getVehicles(),
      maintenance: this.maintenanceService.check(),
      records: this.maintenanceService.getRecords()
    }).subscribe({
      next: ({ activities, deliveries, riders, vehicles, maintenance, records }) => {
        this.activities = activities || [];
        this.deliveries = deliveries || [];
        this.riders = riders || [];
        this.vehicles = vehicles || [];
        this.maintenance = maintenance || [];
        this.records = records || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load the logistics control tower.', 'error');
      }
    });
  }

  get pendingActivities(): Activity[] {
    return this.byStatus('Pending');
  }

  get assignedActivities(): Activity[] {
    return this.byStatus('Assigned');
  }

  get transitActivities(): Activity[] {
    return this.byStatus('In Transit');
  }

  get deliveredActivities(): Activity[] {
    return this.byStatus('Delivered').slice(0, 6);
  }

  get inactiveRiders(): any[] {
    return this.riders.filter((row) => !row?.isActive).slice(0, 6);
  }

  get unassignedVehicles(): any[] {
    return this.vehicles
      .filter((row) => !row?.riderName)
      .slice(0, 6);
  }

  get maintenanceAlerts(): MaintenanceStatusDto[] {
    return this.maintenance
      .filter((row) => row?.needsMaintenance)
      .sort((a, b) => Number(b.daysSinceLastMaintenance || 0) - Number(a.daysSinceLastMaintenance || 0))
      .slice(0, 6);
  }

  get recentMaintenanceRecords(): MaintenanceRecordDto[] {
    return [...this.records]
      .sort((a, b) => new Date(b.serviceDate || 0).getTime() - new Date(a.serviceDate || 0).getTime())
      .slice(0, 6);
  }

  get cashExposure(): number {
    return this.deliveries
      .filter((row) => !row?.isPaid)
      .reduce((sum, row) => sum + Number(row?.amount || 0), 0);
  }

  get deliveredCount(): number {
    return this.activities.filter((row) => String(row?.status || '').toLowerCase() === 'delivered').length;
  }

  private byStatus(status: string): Activity[] {
    return this.activities.filter((row) => String(row?.status || '').toLowerCase() === status.toLowerCase()).slice(0, 8);
  }
}
